/**
 * Camada 1 · modal.
 *
 * Construido sobre o <dialog> nativo, e nao sobre uma div com position fixed. O elemento
 * nativo ja resolve, sem uma linha nossa, tres coisas que costumam ser reimplementadas
 * errado: prender o foco dentro do modal, fechar no Esc, e tornar inerte o resto da
 * pagina para o leitor de tela. Reescrever isso a mao e o caminho classico para um modal
 * que o teclado atravessa sem perceber.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
// O DESENHO VEM DA PECA, e nao um caractere: emoji e simbolo Unicode sao proibidos na
// plataforma, e o botao de fechar e um dos lugares onde a fonte que falta mais aparece.
import { IconeFechar } from './Icones.js';

export interface ModalProps {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly titulo: string;
  readonly children: ReactNode;
  /** Rodape de acoes. Fora do children para o modal controlar o espaco e a ordem. */
  readonly rodape?: ReactNode;
}

export function Modal({ aberto, aoFechar, titulo, children, rodape }: ModalProps) {
  /**
   * O IDENTIFICADOR DO TÍTULO É ÚNICO POR MODAL, e antes era fixo (`cx-modal-titulo`).
   *
   * O DEFEITO QUE ISTO CONSERTA, medido em 07/10/2026 na área da cliente: com DOIS modais abertos
   * ao mesmo tempo (o aviso de erro por cima da confirmação de compromisso), os dois títulos
   * tinham o mesmo identificador. O `aria-labelledby` resolve pelo PRIMEIRO que encontra no
   * documento, então o segundo diálogo, cujo título é "Aviso", era anunciado com o nome do
   * primeiro, "Confirma esse compromisso?".
   *
   * QUEM PAGA ISSO É QUEM USA LEITOR DE TELA: ela ouve o título errado e não sabe a qual pergunta
   * está respondendo. A suíte pegou porque procura o diálogo pelo nome, que é como a tecnologia
   * assistiva o enxerga.
   */
  const idDoTitulo = useId();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // showModal e nao show: so a versao modal prende o foco e escurece o fundo. `show`
    // abriria um painel que o Tab atravessa para os campos de tras.
    if (aberto && !dialog.open) dialog.showModal();
    if (!aberto && dialog.open) dialog.close();
  }, [aberto]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // O Esc fecha o <dialog> por conta propria, sem avisar o React. Sem ouvir o evento
    // `close`, o estado do componente continuaria dizendo "aberto" com o modal fechado,
    // e o proximo clique no botao nao abriria nada.
    const aoFecharNativo = () => aoFechar();
    dialog.addEventListener('close', aoFecharNativo);
    return () => dialog.removeEventListener('close', aoFecharNativo);
  }, [aoFechar]);

  return (
    <dialog
      ref={ref}
      // O backdrop e estilizado pelo pseudo-elemento nativo, que o Tailwind alcanca com
      // o prefixo backdrop:.
      className="cx-cartao m-auto w-[min(32rem,92vw)] p-0 backdrop:bg-superficie-inversa/60"
      aria-labelledby={idDoTitulo}
    >
      <header className="flex items-center justify-between gap-cx-4 border-b border-borda px-cx-5 py-cx-4">
        <h2 id={idDoTitulo} className="text-cx-lg font-semibold text-texto">
          {titulo}
        </h2>
        <button
          type="button"
          onClick={aoFechar}
          // O rotulo escrito importa: um X sozinho e adivinhacao para o leitor de tela.
          aria-label="Fechar"
          className="min-h-toque min-w-toque rounded-cx-md text-texto-suave hover:bg-superficie-afundada"
        >
          {/* O XIS E DESENHADO, e nunca o caractere.
              Ate 08/09/2026 esta linha escrevia o simbolo Unicode direto, e ele e proibido
              na plataforma pela mesma razao do emoji: nem toda fonte o tem, e onde falta o
              navegador desenha um retangulo vazio no lugar do botao de fechar. Ele tambem
              muda de desenho e de peso por fonte, entao o botao saia diferente por sistema
              dentro de um chassi que deveria ser identico em todos os produtos.
              O `IconeFechar` ja existia no mapa `ICONES`, e ninguem o usava aqui. */}
          <IconeFechar aria-hidden="true" className="h-cx-4 w-cx-4" />
        </button>
      </header>

      <div className="px-cx-5 py-cx-4 text-cx-sm text-texto">{children}</div>

      {rodape && (
        <footer className="flex justify-end gap-cx-2 border-t border-borda px-cx-5 py-cx-4">
          {rodape}
        </footer>
      )}
    </dialog>
  );
}
